import {
  Box,
  FlexBox,
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
} from '@dixa/lib-shared-habla';
import { useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { customFormsApi } from 'apis/customForms';
import { HookFormSelectionListSingle } from 'components/hookForm';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import { FlowMessengerNodeCustomForm } from '../../../flow.types';

import { ArchivedFormWarning } from './archivedFormWarning';

type FormValues = {
  formId: string;
};

type Props = FlowManagerEditModalComponentT<FlowMessengerNodeCustomForm>;

export function MessengerCustomFormNodeModal({
  replaceNode,
  triggerClose,
  node,
}: Props): JSX.Element {
  const { data: response } = customFormsApi.useReadFormsQuery();

  const forms = response?.data.forms ?? [];

  const currentNodeFormId = node.payload?.formId;
  const currentNodeFormTitle = node.payload?.formTitle;
  const isSelectedFormArchived =
    currentNodeFormId &&
    forms.length > 0 &&
    forms.find(({ id }) => id === currentNodeFormId) === undefined;

  const { control, formState, handleSubmit } = useForm<FormValues>({
    defaultValues: { formId: currentNodeFormId },
  });

  const handleSaveClick = (values: FormValues) => {
    const formTitle =
      forms?.find(({ id }) => id === values.formId)?.label || '';
    replaceNode({
      ...node,
      childIds: [],
      badges: [],
      icon: '',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.MESSENGER_FORM,
      overlay: [],
      payload: {
        formId: values.formId,
        formTitle,
        nextNodeId: node.payload?.nextNodeId || uuid(),
      },
      text: formTitle,
    });
    triggerClose();
  };

  return (
    <Box width="500px">
      <form onSubmit={handleSubmit(handleSaveClick)}>
        <ModalHead title="Forms" onClose={triggerClose}>
          Once the customer fills out the form, all of their information will be
          saved within the conversation.
        </ModalHead>

        <ModalBody>
          <FlexBox flexDirection="column" alignItems="center" gap="400">
            {isSelectedFormArchived && (
              <ArchivedFormWarning formTitle={currentNodeFormTitle} />
            )}
            <FormGrid>
              <FormField>
                <HookFormSelectionListSingle
                  name="formId"
                  label="Form"
                  rules={{
                    required: { value: true, message: 'Please select a form' },
                    validate: (value) => {
                      const form = forms?.find(({ id }) => id === value);
                      if (!form) {
                        return 'Please select a valid form';
                      }

                      if (form.has_attributes === false) {
                        return 'The selected form is empty. To use it, please edit and add fields.';
                      }

                      return true;
                    },
                  }}
                  disabled={formState.isSubmitting}
                  control={control}
                  componentProps={{
                    getItemTitleByValue: (value) =>
                      forms?.find(({ id }) => id === value)?.label,
                  }}
                >
                  {forms?.map(({ id, label }) => {
                    return (
                      <SelectionList.Item key={id} value={id}>
                        {label}
                      </SelectionList.Item>
                    );
                  })}
                </HookFormSelectionListSingle>
              </FormField>
            </FormGrid>
          </FlexBox>
        </ModalBody>

        <ModalFoot
          confirmOnClick={handleSubmit(handleSaveClick)}
          confirmName="Save"
          cancelOnClick={triggerClose}
          confirmProps={{
            disabled: !formState.isDirty,
            isLoading: formState.isSubmitting,
            loadingIndicator: 'Saving...',
          }}
        />
      </form>
    </Box>
  );
}
